// ─────────────────────────────────────────────────────────────
// Routes — single source of truth for all navigation paths
// ─────────────────────────────────────────────────────────────

export const ROUTES = {
  HOME:         '/',
  LOGIN:        '/login',
  REGISTER:     '/register',
  EXPLORE:      '/explore',
  SEARCH:       '/search',
  UPLOAD:       '/upload',
  PHOTO:        '/photos/:id',
  PROFILE:      '/profile/:username',
  SETTINGS:     '/settings',
  ABOUT:        '/about',
  NOT_FOUND:    '*',
} as const

export const buildRoute = {
  photo:   (id: string)       => `/photos/${id}`,
  profile: (username: string) => `/profile/${username}`,
  search:  (q: string)        => `/search?q=${encodeURIComponent(q)}`,
  tag:     (tag: string)      => `/explore?tag=${encodeURIComponent(tag)}`,
} as const

// ─────────────────────────────────────────────────────────────
// Storage — bucket names and path helpers
// ─────────────────────────────────────────────────────────────

export const STORAGE_BUCKETS = {
  PHOTOS:  'photos',
  AVATARS: 'avatars',
} as const

export const storagePaths = {
  original:  (userId: string, filename: string) => `originals/${userId}/${filename}`,
  thumbnail: (userId: string, filename: string) => `thumbnails/${userId}/${filename}`,
  avatar:    (userId: string, filename: string) => `${userId}/${filename}`,
} as const

// ─────────────────────────────────────────────────────────────
// Query keys — used by TanStack Query for cache invalidation
// ─────────────────────────────────────────────────────────────

export const QUERY_KEYS = {
  PHOTOS:         ['photos']                            as const,
  PHOTO:          (id: string) => ['photos', id]        as const,
  PHOTOS_BY_USER: (userId: string) => ['photos', 'user', userId] as const,
  PHOTOS_BY_TAG:  (tag: string)    => ['photos', 'tag', tag]     as const,
  POPULAR_PHOTOS: ['photos', 'popular']                 as const,
  SEARCH:         (query: string)  => ['search', query] as const,
  PROFILE:        (username: string) => ['profile', username] as const,
  TAGS:           ['tags']                              as const,
} as const

// ─────────────────────────────────────────────────────────────
// Pagination
// ─────────────────────────────────────────────────────────────

export const PAGINATION = {
  DEFAULT_PAGE_SIZE: 20,
  RELATED_PHOTOS:    6,
  PROFILE_PHOTOS:    12,
} as const

// ─────────────────────────────────────────────────────────────
// File upload limits
// ─────────────────────────────────────────────────────────────

export const UPLOAD_LIMITS = {
  MAX_FILE_SIZE_MB:   30,
  MAX_FILE_SIZE_BYTES: 30 * 1024 * 1024,
  THUMBNAIL_MAX_WIDTH_PX: 800,
  THUMBNAIL_QUALITY:      0.8,
  ACCEPTED_MIME_TYPES: ['image/jpeg', 'image/png', 'image/webp', 'image/gif'] as const,
  ACCEPTED_EXTENSIONS: '.jpg,.jpeg,.png,.webp,.gif',
} as const

// ─────────────────────────────────────────────────────────────
// UI
// ─────────────────────────────────────────────────────────────

export const SKELETON_COUNT = {
  PHOTO_GRID: 12,
  RELATED:    4,
} as const

export const SORT_OPTIONS = [
  { label: 'Newest',  value: 'newest'  },
  { label: 'Popular', value: 'popular' },
] as const

// ─────────────────────────────────────────────────────────────
// SEO
// ─────────────────────────────────────────────────────────────

export const SEO = {
  SITE_NAME:        'UnsplashKH',
  DEFAULT_TITLE:    'UnsplashKH — High quality photos, free to use',
  DEFAULT_DESC:     'Discover and download stunning high-resolution photos shared by talented creators.',
  TWITTER_HANDLE:   '@UnsplashKH',
  OG_IMAGE:         '/og-image.jpg',
} as const
